import { Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import matchingRoundsAPI, {
  MatchingRoundId,
  MatchLog,
  MatchLogFilterParams,
} from '../../api/matching-rounds';
import { PageLoader, SnackMessage } from '../../components/common';
import LogsHeader from '../../components/matching-rounds/logs/logs-header';
import { COLORS } from '../../theme/variables';

interface MatchingRoundLogsPageProps {
  roundId: MatchingRoundId;
}

const PAGE_SIZE = 100;

function formatDateTime(dateTime: string): string {
  try {
    return new Date(dateTime).toLocaleString();
  } catch (_e) {
    return dateTime;
  }
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 40,
    minHeight: 0,
    overflow: 'hidden',
  },
  logPanel: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    padding: '0 0 12px 0',
  },
  logRow: {
    display: 'flex',
    padding: '8px 0',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
  },
  dateColumn: {
    flexShrink: 0,
    width: 180,
    fontSize: 13,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    paddingRight: 16,
  },
  messageColumn: {
    flex: 1,
    fontSize: 13,
    color: COLORS.COLOR_TEXT_BASE,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    lineHeight: 1.5,
  },
  loader: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  listLoader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    minHeight: 300,
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  retryButton: {
    marginTop: 12,
  },
});

const MatchingRoundLogsPage = memo(function MatchingRoundLogsPage({
  roundId,
}: MatchingRoundLogsPageProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [logs, setLogs] = useState<MatchLog[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [hasAnyLogs, setHasAnyLogs] = useState(false);

  const isLoadingPageRef = useRef(false);
  const loadGenRef = useRef(0);
  const currentPageRef = useRef(currentPage);
  const searchRef = useRef(search);
  const skipInitialRef = useRef(true);

  currentPageRef.current = currentPage;
  searchRef.current = search;

  const logPanelRef = useRef<HTMLDivElement>(null);

  const buildFilterParams = useCallback(
    (page: number): MatchLogFilterParams => {
      const params: MatchLogFilterParams = {
        pageNumber: page,
        pageSize: PAGE_SIZE,
      };
      if (searchRef.current) {
        params.search = searchRef.current;
      }
      return params;
    },
    [],
  );

  const loadPage = useCallback(
    async (page: number) => {
      if (isLoadingPageRef.current) return;
      isLoadingPageRef.current = true;
      const gen = loadGenRef.current;
      try {
        setIsLoadingPage(true);
        setLoadError(false);
        const data = await matchingRoundsAPI.filterLogs(
          roundId,
          buildFilterParams(page),
        );
        if (gen !== loadGenRef.current) return;
        if (data.length === 0) {
          setHasMorePages(false);
        } else {
          setHasAnyLogs(true);
          setLogs((prev) => [...prev, ...data]);
          setCurrentPage(page);
        }
      } catch (e: unknown) {
        if (gen !== loadGenRef.current) return;
        const msg = (e as AxiosError)?.response?.data?.message;
        setLoadError(true);
        enqueueSnackbar('Failed to load logs', {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: msg,
            }),
          variant: 'error',
        });
      } finally {
        if (gen === loadGenRef.current) {
          isLoadingPageRef.current = false;
          setIsLoadingPage(false);
          setIsInitialLoading(false);
        }
      }
    },
    [roundId, enqueueSnackbar, buildFilterParams],
  );

  // Initial load
  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  // Infinite scroll
  useEffect(() => {
    if (isInitialLoading) return;
    const container = logPanelRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        hasMorePages &&
        !isLoadingPageRef.current &&
        container.scrollHeight - container.scrollTop <=
        container.clientHeight + 2000
      ) {
        loadPage(currentPageRef.current + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [hasMorePages, loadPage, isInitialLoading]);

  // Reload when search changes — skip initial mount
  useEffect(() => {
    if (skipInitialRef.current) {
      skipInitialRef.current = false;
      return;
    }
    loadGenRef.current++;
    isLoadingPageRef.current = false;
    setCurrentPage(0);
    setHasMorePages(true);
    setLogs([]);
    loadPage(0);
  }, [search, loadPage]);

  const handleRetry = useCallback(() => {
    setCurrentPage(0);
    setHasMorePages(true);
    setIsInitialLoading(true);
    setLoadError(false);
    isLoadingPageRef.current = false;
    setLogs([]);
    loadPage(0);
  }, [loadPage]);

  // Initial loading
  if (isInitialLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.loader}>
          <PageLoader />
        </div>
      </div>
    );
  }

  const showCenteredSpinner = isLoadingPage && logs.length === 0;
  const showBottomSpinner = isLoadingPage && logs.length > 0;

  return (
    <div className={classes.container} data-testid='round-logs-page'>
      {hasAnyLogs && (
        <LogsHeader search={search} onSearchChange={setSearch} />
      )}
      <div
        className={classes.logPanel}
        ref={logPanelRef}
        data-testid='logs-panel'
      >
        {showCenteredSpinner ? (
          <div className={classes.listLoader}>
            <CircularProgress />
          </div>
        ) : loadError && logs.length === 0 ? (
          <div className={classes.emptyState}>
            <div style={{ textAlign: 'center' }}>
              <ErrorOutlineIcon color='error' style={{ fontSize: 48, marginBottom: 8 }} />
              <Typography variant='body1' color='error' gutterBottom>
                Failed to load logs
              </Typography>
              <Button
                variant='outlined'
                color='primary'
                onClick={handleRetry}
                className={classes.retryButton}
                data-testid='logs-retry'
              >
                Retry
              </Button>
            </div>
          </div>
        ) : logs.length === 0 && !isLoadingPage ? (
          <div className={classes.emptyState}>
            <Typography variant='body1' color='textSecondary'>
              {search
                ? 'No logs found for the current search.'
                : 'No logs available for this matching round.'}
            </Typography>
          </div>
        ) : (
          <>
            {logs.map((log) => (
              <div
                key={log.id}
                className={classes.logRow}
                data-testid='log-row'
              >
                <div className={classes.dateColumn}>
                  {formatDateTime(log.creationDate)}
                </div>
                <div className={classes.messageColumn}>{log.description}</div>
              </div>
            ))}
            {showBottomSpinner && (
              <div className={classes.loader}>
                <CircularProgress size={24} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export default MatchingRoundLogsPage;
